import { DropdownListItem, type IconType } from '@verantos-dev/shared-components';
import { Attribute, SimpleEventType } from '@verantos-dev/types-cohorts';
import { ConceptGroup } from '@verantos-dev/types-concepts';

import { transformConceptGroupIdToOptionsValue } from '../../utils/getNewFilterForType';
import rules from '../rules';

const isNewConceptGroup = (conceptGroups: ConceptGroup[]) =>
  'domains' in (conceptGroups?.[0] ?? {}) && 'owner' in (conceptGroups?.[0] ?? {});

const isVerantosCreated = (conceptGroup: ConceptGroup) =>
  conceptGroup.owner?.toLowerCase?.() === 'verantos';

function getConceptGroupFilterConfig(
  attribute: Attribute
): Record<string, { include: string[]; exclude: string[] }> {
  return (
    (
      rules.conceptGroupFilters as Record<
        string,
        Record<string, { include: string[]; exclude: string[] }>
      >
    )[attribute] ?? { default: { include: [], exclude: [] } }
  );
}

const processConceptGroups = (
  conceptGroups: ConceptGroup[],
  attribute: Attribute,
  simpleEventType: SimpleEventType
): DropdownListItem[] => {
  let filtered = conceptGroups;
  const isFiltered = isNewConceptGroup(conceptGroups);
  if (isFiltered) {
    const config = getConceptGroupFilterConfig(attribute);
    const { include, exclude } = config[simpleEventType] ?? config.default;

    filtered = conceptGroups.filter((conceptGroup) => {
      const domains = conceptGroup.domains?.split(/,\s*/).map((domain) => domain.trim()) || [];

      // Include if at least one domain qualifies the include rule (or include is empty)
      const hasQualifyingInclude =
        include.length === 0 || domains.some((groupDomain) => include.includes(groupDomain));

      // Exclude only if all domains are in the exclude list
      const allDomainsExcluded =
        domains.length > 0 && domains.every((groupDomain) => exclude.includes(groupDomain));

      return hasQualifyingInclude && !allDomainsExcluded;
    });
  }

  const processed =
    filtered?.map((conceptGroup) => ({
      displayValue:
        conceptGroup.conceptGroupName.charAt(0).toUpperCase() +
        conceptGroup.conceptGroupName.slice(1),
      value: transformConceptGroupIdToOptionsValue(conceptGroup.conceptGroupId),
      ...(isFiltered
        ? {
            icon: isVerantosCreated(conceptGroup) ? 'verantosLogo' : ('owner' as IconType),
            actionButtonIcon: 'fullscreen' as IconType,
          }
        : {}),
    })) ?? [];

  return processed;
};

export default processConceptGroups;
